/*
 * The one search route (protocol.md §3.2), typed once. It is a POST and not a GET because a query
 * string in a request line reaches the access log, the Referer header and the browser history, and
 * a search term is never logged (spec 10, A26) — which is also why the page keeps the term in its
 * own state and never in the URL.
 *
 * The shapes are hand-written: `internal/core/search` answers with its own view structs, and tygo
 * generates only the two `wire` packages (tygo.yaml). Field names come from those structs verbatim.
 */

import { api, WireError } from "../net/api.ts"

/** How the hits are ordered: newest first, or best match inside a bounded recent window. */
export type SearchMode = "recent" | "best"

/** One run of a snippet. `m` marks the run the query matched; the client never parses offsets. */
export interface SnippetPart {
	t: string
	m?: boolean
}

/**
 * One hit. It carries the ids needed to open the document and nothing about it the caller could
 * not already read: the pre-filter runs inside the same statement as the match, so a row that
 * arrives here is a row this account may see.
 */
export interface SearchResult {
	kind: number
	sourceId: number
	scopeKind: string
	scopeId: number
	scopeSeq: number
	authorUserId: number
	createdAtMs: number
	flags: number
	joined: boolean
	score: number
	snippet: { parts: SnippetPart[] }
	/**
	 * What the family's owner says the hit is called and where it opens (S-09 §3.3): a document's
	 * section, a drive item's name. Hits that share `itemKey` are one card. A message has none, and a
	 * describer that failed leaves it out rather than the page.
	 */
	hit?: SearchHit
}

/** A described hit (internal/core/search `Hit`). `path` is the address the hit opens. */
export interface SearchHit {
	title: string
	/** Where the item lives: its drive, then the folders above it (" › "). */
	place: string
	/** The heading path of a document's section ("Pricing › Enterprise"); absent for a name. */
	section?: string
	path: string
	itemKey: string
	/** "document", "folder", "file" — the drive's item kinds. */
	itemKind: string
	/** Set on a hit of a deleted pass: when and by whom it went to the trash. */
	deleted?: SearchDeleted
}

/**
 * When a hit of a deleted pass went to the trash (ms), who moved it there, the person an AI chat's
 * trash was asked by (absent for a person's own), and when it is deleted for good (ms).
 */
export interface SearchDeleted {
	at: number
	by: number
	for?: number
	until: number
}

/**
 * Which pass a search runs: absent is the ordinary search; "recent" reads what went to the trash of
 * the reader's drives within the last year, "all" everything the trash still keeps. A deleted pass is
 * its own request, sent after the live answer and never beside it (S-09-D4).
 */
export type DeletedPass = "recent" | "all"

/**
 * One word or phrase of the query, as it was typed. The client never folds a term — the server's
 * analyser is the only thing that decides what a word matches (A26).
 */
export interface SearchTerm {
	text: string
	phrase: boolean
	prefix: boolean
	negate: boolean
}

/**
 * What the server made of the query: every operator resolved, and every `foo:bar` it has no
 * operator for reported as text it searched for. The filter bar is a renderer of this and not a
 * second parser, which is the only way it can never disagree with what the search actually did.
 */
export interface ParsedQuery {
	terms: SearchTerm[]
	/** The accounts `from:` resolved to. A handle nobody has resolves to nothing. */
	from: number[]
	/** The conversations `in:` resolved to, already intersected with what this reader may read. */
	in: { scopeKind: string; scopeId: number }[]
	afterMs: number
	beforeMs: number
	hasFile: boolean
	/** The `type:` words the server applied (document, file, folder); absent from an older server. */
	types?: string[]
	unknown: string[]
}

/** What the route answers. `total` is capped: "more than N" costs less than an exact number. */
export interface SearchResponse {
	parsed: ParsedQuery
	results: SearchResult[]
	total: number
	totalCapped: boolean
	substringFallback: boolean
	truncated: boolean
	nextCursor: string
	tookMs: number
}

export interface SearchRequest {
	q: string
	mode: SearchMode
	limit?: number
	cursor?: string
	/**
	 * The families to search, by the names their sources registered ("message", "document",
	 * "drive.item"); absent asks every family. A section of the page asks for its own (S-09 D5).
	 */
	kinds?: readonly string[]
	deleted?: DeletedPass
}

/**
 * The bits of `SearchResult.flags` a row is drawn from (internal/core/search/doc.go). They are a
 * filter and a badge, never a permission: a hit that arrives is one this account may read.
 */
export const FLAG_FILE = 1 << 0
/** A drive item's name row names a document; every section of a document carries it too. */
export const FLAG_DOCUMENT = 1 << 5
/** A drive item's name row names a folder. */
export const FLAG_FOLDER = 1 << 6

/** How many hits one page asks for; the server caps it far above this. */
export const PAGE_SIZE = 25

export function search(request: SearchRequest): Promise<SearchResponse> {
	return api.post<SearchResponse>("/api/search", {
		q: request.q,
		mode: request.mode,
		limit: request.limit ?? PAGE_SIZE,
		cursor: request.cursor ?? "",
		...(request.kinds === undefined ? {} : { kinds: request.kinds }),
		...(request.deleted === undefined ? {} : { deleted: request.deleted }),
	})
}

/**
 * True when the failure means "this instance has no search", not "this search failed". The two
 * need different sentences: a person who is told there are no results stops looking, and a person
 * whose instance cannot search has to be told that instead of being left to conclude it.
 */
export function searchIsUnavailable(err: unknown): boolean {
	if (!(err instanceof WireError)) return false
	if (err.status === 404 || err.status === 501) return true
	return err.code === "unavailable" || err.code === "notImplemented"
}
